import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Bell, CheckCheck, Settings, History, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotificationCenter } from '@/stores/notificationCenterStore';
import { NotificationItem } from './NotificationItem';
import { CATEGORY_INFO, type NotificationCategory } from '../types/notification.types';

// ============================================================================
// Types
// ============================================================================

interface NotificationCenterProps {
  className?: string;
}

// ============================================================================
// Category Filter Chips
// ============================================================================

const CategoryChips: React.FC<{
  activeCategory: NotificationCategory | null;
  onSelect: (category: NotificationCategory | null) => void;
  stats?: Record<string, number>;
}> = ({ activeCategory, onSelect, stats }) => {
  const { t } = useTranslation();
  const categories: (NotificationCategory | null)[] = [
    null, // All
    'FISCAL',
    'BILLING',
    'PAYMENTS',
    'TREASURY',
    'AI_INSIGHTS',
    'SYSTEM',
  ];

  return (
    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
      {categories.map((category) => {
        const isActive = activeCategory === category;
        const label = category ? CATEGORY_INFO[category].label : t('common.notifications.all');
        const count = category && stats ? stats[category] || 0 : null;

        return (
          <button
            key={category || 'all'}
            onClick={() => onSelect(category)}
            className={`
              flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium
              transition-all duration-200
              ${isActive
                ? 'bg-teal-600 text-white'
                : 'bg-white/50 text-gray-600 hover:bg-white/80'
              }
            `}
          >
            {label}
            {count !== null && count > 0 && (
              <span className={`ml-1.5 ${isActive ? 'text-teal-100' : 'text-gray-400'}`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ============================================================================
// Empty State
// ============================================================================

const EmptyState: React.FC<{ category: NotificationCategory | null }> = ({ category }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <Bell className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        {t('common.notifications.noNotification')}
      </h3>
      <p className="text-sm text-gray-500">
        {category
          ? t('common.notifications.noNotificationCategory', { category: CATEGORY_INFO[category].label })
          : t('common.notifications.noNotificationGeneral')
        }
      </p>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const NotificationCenter: React.FC<NotificationCenterProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    isOpen,
    close,
    notifications,
    unreadCount,
    isLoading,
    error,
    activeCategory,
    hasMore,
    stats,
    setActiveCategory,
    fetchMoreNotifications,
    markAsRead,
    markAllAsRead,
    dismiss,
    refresh,
  } = useNotificationCenter();

  // Handle scroll for infinite loading
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMore && !isLoading) {
        fetchMoreNotifications();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, isLoading, fetchMoreNotifications]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const categoryStats = stats?.byCategory || {};

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
        onClick={close}
      />

      {/* Drawer */}
      <div
        className={`
          fixed top-0 right-0 h-full w-full max-w-md z-50
          bg-gradient-to-b from-gray-50/95 to-white/95 backdrop-blur-xl
          shadow-2xl
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          ${className}
        `}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/80 backdrop-blur-xl border-b border-gray-100 px-4 py-4 z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-xl">
                <Bell className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{t('common.notifications.title')}</h2>
                {unreadCount > 0 && (
                  <p className="text-xs text-gray-500">
                    {unreadCount > 1 ? t('common.notifications.unreadCountPlural', { count: unreadCount }) : t('common.notifications.unreadCount', { count: unreadCount })}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Refresh Button */}
              <button
                onClick={() => refresh()}
                disabled={isLoading}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                title={t('common.notifications.refresh')}
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>

              {/* Mark All Read */}
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllAsRead()}
                  className="p-2 rounded-lg hover:bg-teal-50 text-gray-500 hover:text-teal-600 transition-colors"
                  title={t('common.notifications.markAllRead')}
                >
                  <CheckCheck className="w-4 h-4" />
                </button>
              )}

              {/* Settings */}
              <button
                onClick={() => {
                  navigate('/settings/notifications');
                  close();
                }}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                title={t('common.notifications.settings')}
              >
                <Settings className="w-4 h-4" />
              </button>

              {/* Close */}
              <button
                onClick={close}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Category Filters */}
          <CategoryChips
            activeCategory={activeCategory}
            onSelect={setActiveCategory}
            stats={categoryStats as Record<string, number>}
          />
        </div>

        {/* Content */}
        <div
          ref={scrollContainerRef}
          className="h-[calc(100%-180px)] overflow-y-auto px-4 py-4"
        >
          {/* Error State */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Notifications List */}
          {notifications.length > 0 ? (
            <div className="space-y-2">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onDismiss={dismiss}
                  onClose={close}
                />
              ))}

              {/* Loading More */}
              {isLoading && (
                <div className="flex justify-center py-4">
                  <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              )}

              {/* End of List */}
              {!hasMore && notifications.length > 5 && (
                <p className="text-center text-xs text-gray-400 py-4">
                  {t('common.notifications.endOfNotifications')}
                </p>
              )}
            </div>
          ) : !isLoading ? (
            <EmptyState category={activeCategory} />
          ) : (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-xl border-t border-gray-100">
          <button
            onClick={() => {
              navigate('/notifications');
              close();
            }}
            className="
              w-full flex items-center justify-center gap-2
              py-2.5 px-4 rounded-xl
              bg-gray-100 hover:bg-gray-200
              text-sm font-medium text-gray-700
              transition-colors duration-200
            "
          >
            <History className="w-4 h-4" />
            {t('common.notifications.viewHistory')}
          </button>
        </div>
      </div>
    </>
  );
};

export default NotificationCenter;
